import type { Dayjs } from 'dayjs';
import type { CustomerOrderBase } from '../contracts/customer-order.js';
import { GiftCardRedemption } from '../models/serializable/gift-card/gift-card-redemption.js';
import { BusinessModuleRef } from '../models/serializable/business-module-ref.js';
import { Discount, DiscountSource, DiscountType } from '../models/serializable/discounts/discount.js';
import { GiftCard, GiftCardStatus } from '../models/serializable/gift-card/gift-card.js';
import { Price } from '../models/serializable/price.js';
import { Result } from './result.js';
import { generateUniqueDatabaseId } from '../models/dto/data-model-utils.js';

export function redeemGiftCard(args: {
  card: GiftCard,
  discounts: readonly Discount[],
  businessRef: BusinessModuleRef,
  creationDate: Dayjs,
}): Result<GiftCard, Error> {
  const { card, discounts, businessRef, creationDate } = args;
  if(!card.businessRef?.isSame(businessRef) || card.status !== GiftCardStatus.Live ||
      !card.expirationDate.isValid() || creationDate.isAfter(card.expirationDate) ||
      !card.maxValue.isPositive() || card.usedValue.currency !== card.currency ||
      card.usedValue.value < 0 || card.remainingValue.value < 0 || discounts.length === 0) {
    return Result.error(new Error('Gift card is not valid for this order.'));
  }

  let spent = Price.zero(card.currency);
  for(const discount of discounts) {
    if(discount.source !== DiscountSource.GiftCard ||
        discount.voucherId !== card.uniqueId || discount.code !== card.code ||
        !discount.businessRef.isSame(businessRef) || discount.type !== DiscountType.Fixed ||
        discount.fixedPrice == null || discount.fixedPrice.currency !== card.currency ||
        !discount.fixedPrice.isPositive() || discount.voucherValue == null ||
        !discount.voucherValue.isSame(discount.fixedPrice)) {
      return Result.error(new Error('Gift card deduction is not valid.'));
    }
    spent = spent.add(discount.fixedPrice);
  }
  if(spent.compareTo(card.remainingValue) > 0) {
    return Result.error(new Error('Gift card has insufficient remaining balance.'));
  }

  return Result.ok(card.copyWith({
    usedValue: card.usedValue.add(spent),
    status: spent.compareTo(card.remainingValue) === 0 ? GiftCardStatus.Used : GiftCardStatus.Live,
  }));
}

export function prepareGiftCardRedemption({ card, discounts, order, creationDate }: {
  card: GiftCard,
  discounts: readonly Discount[],
  order: Pick<CustomerOrderBase, 'uniqueId' | 'businessRef' | 'creationDate'>,
  creationDate: Dayjs,
}): Result<{ card: GiftCard, redemption: GiftCardRedemption }, Error> {
  return redeemGiftCard({ card, discounts, businessRef: order.businessRef, creationDate: order.creationDate })
      .map((updated) => ({
        card: updated,
        redemption: GiftCardRedemption.create({
          uniqueId: generateUniqueDatabaseId(),
          businessRef: order.businessRef,
          giftCardId: card.uniqueId,
          templateId: card.templateId,
          orderId: order.uniqueId,
          amount: updated.usedValue.difference(card.usedValue),
          creationDate,
        }),
      }));
}
